import { useMemo } from 'react'

import { useActiveOrgId } from '~/hooks/org'
import { useOrgBalances } from '~/hooks/useBalances'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { IAddress } from '~/types'
import { TokenBalance } from '~/utils/account'

export type AggregatedBalance = TokenBalance & {
  totalAmount: bigint
  totalUsdValue: number
}

type UseAggregatedBalancesOptions = {
  filterAccountAddresses?: IAddress[]
}

/**
 * Aggregates token balances across all smart accounts in the org.
 * Returns a list of unique tokens with their combined amounts and USD values,
 * sorted by total USD value descending.
 *
 * @param options.filterAccountAddresses - If provided, only aggregate balances from these accounts
 */
export const useAggregatedBalances = (
  options: UseAggregatedBalancesOptions = {},
) => {
  const { filterAccountAddresses } = options
  const activeOrgId = useActiveOrgId()
  const { balances: orgBalances, isLoading } = useOrgBalances(activeOrgId)
  const { convertTokenToUSD } = useTokenPrices()
  const filterAccountAddressesKey = useMemo(
    () =>
      filterAccountAddresses
        ?.map((address) => address.toLowerCase())
        .sort()
        .join('|') ?? '',
    [filterAccountAddresses],
  )
  const normalizedFilterAccountAddresses = useMemo(
    () =>
      filterAccountAddressesKey.length > 0
        ? filterAccountAddressesKey.split('|')
        : null,
    [filterAccountAddressesKey],
  )

  const aggregatedBalances = useMemo(() => {
    const filterSet = normalizedFilterAccountAddresses
      ? new Set(normalizedFilterAccountAddresses)
      : null

    const accountEntries = Object.entries(orgBalances)
    const filteredEntries = filterSet
      ? accountEntries.filter(([address]) =>
          filterSet.has(address.toLowerCase()),
        )
      : accountEntries

    const allBalances = filteredEntries.flatMap(([, b]) => b.balances)

    // Group by token (address + chainId)
    const grouped = allBalances.reduce<
      Record<string, { balances: TokenBalance[]; totalAmount: bigint }>
    >((acc, balance) => {
      const key = `${balance.chainId}-${balance.address.toLowerCase()}`
      if (!acc[key]) {
        acc[key] = { balances: [], totalAmount: 0n }
      }
      acc[key].balances.push(balance)
      acc[key].totalAmount += balance.amount
      return acc
    }, {})

    // Convert to array with aggregated values
    return Object.values(grouped)
      .map(({ balances, totalAmount }): AggregatedBalance => {
        const firstBalance = balances[0]
        const totalUsdValue =
          convertTokenToUSD({
            token: firstBalance.address,
            amount: totalAmount,
            chainId: firstBalance.chainId,
          }) ?? 0

        // Override base TokenBalance fields (amount, usdValue) with aggregated values
        // while also exposing them as totalAmount/totalUsdValue for clarity
        return {
          ...firstBalance,
          amount: totalAmount,
          totalAmount,
          totalUsdValue,
          usdValue: totalUsdValue,
        }
      })
      .sort((a, b) => b.totalUsdValue - a.totalUsdValue)
  }, [orgBalances, convertTokenToUSD, normalizedFilterAccountAddresses])

  return {
    balances: aggregatedBalances,
    isLoading,
  }
}
